import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Grid } from "@mui/material";
import {
  Assignment,
  Phone,
  CalendarToday,
  Person,
  Star,
  Add,
  Delete,
  HighlightOff,
} from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import FileUploader from "components/FileUploader/FileUploader";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { toast } from "react-toastify";
import api from "utils/api";
import { createEHC, updateEHC, getEHCById } from "services/ehcService";

const AddEHC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);

  // Dropdown data states
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [allTutors, setAllTutors] = useState([]);
  const [parents, setParents] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [defaultEHCProduct, setDefaultEHCProduct] = useState(null);
  const [linkedProductName, setLinkedProductName] = useState("");

  const getTutorIdentifier = (tutor) => {
    if (!tutor) return "";
    return String(tutor.id || tutor._id || "");
  };

  const buildTutorOptionData = (tutor) => {
    const tutorId = getTutorIdentifier(tutor);
    if (!tutorId) return null;
    return {
      id: tutorId,
      _id: tutor._id || tutor.id,
      fullName: tutor.fullName || tutor.name || "Unknown Tutor",
      phone: tutor.phone || "",
      email: tutor.email || "",
    };
  };

  const getCurrentDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTimeString = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    // IDs
    studentId: "",
    tutorId: "",
    userId: "",
    parentid: "",
    productId: "",

    // Discovery Call
    discoverCall: {
      callDoneBy: "",
      callDate: "",
      callStatus: "PENDING",
      remarks: "",
    },

    // EHC Coordination
    ehcCoordination: {
      supervisorId: "",
      supervisorName: "",
      ehcName: "",
      timeSlot: "",
      date: "",
      remarks: "",
    },

    // SRC - Strategic Roadmapping And Consultation
    srcConsultation: {
      meetingDate: "",
      meetingTime: "",
      meetingLink: "",
      academicCoach: "",
    },

    shortTermGoals: [""],
    longTermGoals: [""],

    // Section A - Content Knowledge (Multiple subjects with dynamic add/remove)
    contentKnowledge: [{ name: "", understandingLevel: 0, remarks: "" }],

    // Section B - Core Parameters (5 parameters)
    coreParameters: {
      overallFoundation: { rating: 1, remarks: "" },
      memoryRetention: { rating: 1, remarks: "" },
      confidenceInExplaining: { rating: 1, remarks: "" },
      writingSkills: { rating: 1, remarks: "" },
      timeManagement: { rating: 1, remarks: "" },
    },

    // Section C - Behavioural Parameters (Yes/No)
    behaviouralParameters: {
      regularInPractice: "Yes",
      asksDoubtsActively: "Yes",
      followsStudyPlan: "Yes",
    },

    // Section D - Parents Review
    parentsReview: {
      regularityOfEducator: "",
      studentSelfStudyTime: "",
      inTouchWithTutor: "Yes",
      concernsRaisedByParents: "",
    },

    // SWOT Assessment
    swotAssessment: {
      strength: "",
      weakness: "",
      opportunity: "",
      threats: "",
    },

    status: "DRAFT",
  });

  const [coachImageFiles, setCoachImageFiles] = useState([]);
  const [coachImagePreview, setCoachImagePreview] = useState(null);

  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "10px",
    p: 3,
    mb: 3,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    border: "1px solid #f5f5f5",
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleSubjectChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedSubjects = [...prev.contentKnowledge];
      updatedSubjects[index] = { ...updatedSubjects[index], [field]: value };
      return { ...prev, contentKnowledge: updatedSubjects };
    });
  };

  const addSubject = () => {
    setFormData((prev) => ({
      ...prev,
      contentKnowledge: [
        ...prev.contentKnowledge,
        { name: "", understandingLevel: 5, remarks: "" },
      ],
    }));
  };

  const removeSubject = (index) => {
    if (formData.contentKnowledge.length <= 1) {
      toast.error("At least one subject is required");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      contentKnowledge: prev.contentKnowledge.filter((_, i) => i !== index),
    }));
  };

  const handleGoalItemChange = (section, index, value) => {
    setFormData((prev) => {
      const updatedGoals = [...(prev[section] || [])];
      updatedGoals[index] = value;
      return { ...prev, [section]: updatedGoals };
    });
  };

  const addGoalItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: [...(prev[section] || []), ""],
    }));
  };

  const removeGoalItem = (section, index, sectionLabel) => {
    if ((formData[section] || []).length <= 1) {
      toast.error(`At least one ${sectionLabel} point is required`);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [section]: (prev[section] || []).filter((_, i) => i !== index),
    }));
  };

  // Helper function to clean empty strings and null values from nested objects
  const cleanEmptyFields = (data) => {
    const cleaned = { ...data };

    // Clean nested objects - remove empty string ObjectId references
    if (cleaned.discoverCall) {
      if (!cleaned.discoverCall.callDoneBy || cleaned.discoverCall.callDoneBy === "") {
        delete cleaned.discoverCall.callDoneBy;
      }
      if (!cleaned.discoverCall.callDate) {
        delete cleaned.discoverCall.callDate;
      }
      if (!cleaned.discoverCall.remarks || cleaned.discoverCall.remarks === "") {
        delete cleaned.discoverCall.remarks;
      }
    }

    if (cleaned.ehcCoordination) {
      if (!cleaned.ehcCoordination.supervisorId || cleaned.ehcCoordination.supervisorId === "") {
        delete cleaned.ehcCoordination.supervisorId;
      }
      if (
        !cleaned.ehcCoordination.supervisorName ||
        cleaned.ehcCoordination.supervisorName === ""
      ) {
        delete cleaned.ehcCoordination.supervisorName;
      }
      if (!cleaned.ehcCoordination.ehcName || cleaned.ehcCoordination.ehcName === "") {
        delete cleaned.ehcCoordination.ehcName;
      }
      if (!cleaned.ehcCoordination.timeSlot || cleaned.ehcCoordination.timeSlot === "") {
        delete cleaned.ehcCoordination.timeSlot;
      }
      if (!cleaned.ehcCoordination.date || cleaned.ehcCoordination.date === "") {
        delete cleaned.ehcCoordination.date;
      }
      if (!cleaned.ehcCoordination.remarks || cleaned.ehcCoordination.remarks === "") {
        delete cleaned.ehcCoordination.remarks;
      }
    }

    if (cleaned.srcConsultation) {
      if (!cleaned.srcConsultation.meetingDate) {
        delete cleaned.srcConsultation.meetingDate;
      }
      if (!cleaned.srcConsultation.meetingTime || cleaned.srcConsultation.meetingTime === "") {
        delete cleaned.srcConsultation.meetingTime;
      }
      if (!cleaned.srcConsultation.meetingLink || cleaned.srcConsultation.meetingLink === "") {
        delete cleaned.srcConsultation.meetingLink;
      }
      if (!cleaned.srcConsultation.academicCoach || cleaned.srcConsultation.academicCoach === "") {
        delete cleaned.srcConsultation.academicCoach;
      }
      if (Object.keys(cleaned.srcConsultation).length === 0) {
        delete cleaned.srcConsultation;
      }
    }

    ["shortTermGoals", "longTermGoals"].forEach((section) => {
      if (Array.isArray(cleaned[section])) {
        cleaned[section] = cleaned[section]
          .map((point) => (typeof point === "string" ? point.trim() : ""))
          .filter((point) => point !== "");

        if (cleaned[section].length === 0) {
          delete cleaned[section];
        }
      }
    });

    // Clean userId if empty
    if (!cleaned.userId || cleaned.userId === "") {
      delete cleaned.userId;
    }

    // Clean contentKnowledge array - remove empty subjects and _id field
    if (cleaned.contentKnowledge && Array.isArray(cleaned.contentKnowledge)) {
      cleaned.contentKnowledge = cleaned.contentKnowledge
        .filter((subject) => subject.name && subject.name.trim() !== "")
        .map((subject) => {
          // Remove MongoDB _id field if present
          const { _id, ...subjectWithoutId } = subject;
          return subjectWithoutId;
        });
      // If no subjects, remove the array
      if (cleaned.contentKnowledge.length === 0) {
        delete cleaned.contentKnowledge;
      }
    }

    // Clean empty strings from all sections
    ["coreParameters", "behaviouralParameters", "parentsReview", "swotAssessment"].forEach(
      (section) => {
        if (cleaned[section]) {
          Object.keys(cleaned[section]).forEach((key) => {
            const value = cleaned[section][key];
            if (typeof value === "object" && value !== null) {
              // For nested objects like coreParameters
              Object.keys(value).forEach((nestedKey) => {
                if (value[nestedKey] === "" || value[nestedKey] === null) {
                  delete value[nestedKey];
                }
              });
              // If nested object is empty, remove it
              if (Object.keys(value).length === 0) {
                delete cleaned[section][key];
              }
            } else if (value === "" || value === null) {
              delete cleaned[section][key];
            }
          });
          // If section is empty, remove it
          if (Object.keys(cleaned[section]).length === 0) {
            delete cleaned[section];
          }
        }
      }
    );

    return cleaned;
  };

  const handleCoreParamChange = (paramKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      coreParameters: {
        ...prev.coreParameters,
        [paramKey]: { ...prev.coreParameters[paramKey], [field]: value },
      },
    }));
  };

  const handleSrcDateChange = (meetingDate) => {
    const today = getCurrentDateString();
    if (meetingDate && meetingDate < today) {
      toast.error("Past date is not allowed");
      return;
    }

    setFormData((prev) => {
      const nextSrc = { ...prev.srcConsultation, meetingDate };
      if (
        meetingDate === today &&
        nextSrc.meetingTime &&
        nextSrc.meetingTime < getCurrentTimeString()
      ) {
        nextSrc.meetingTime = getCurrentTimeString();
      }
      return {
        ...prev,
        srcConsultation: nextSrc,
      };
    });
  };

  const handleSrcTimeChange = (meetingTime) => {
    const today = getCurrentDateString();
    if (
      formData.srcConsultation.meetingDate === today &&
      meetingTime &&
      meetingTime < getCurrentTimeString()
    ) {
      toast.error("Past time is not allowed for today");
      return;
    }
    handleNestedChange("srcConsultation", "meetingTime", meetingTime);
  };

  // Fetch initial dropdown data
  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        // Fetch default EHC product for auto-linking during create
        try {
          const defaultProductRes = await api.get("/ehc/default-product");
          const defaultProduct = defaultProductRes?.data?.data || null;
          setDefaultEHCProduct(defaultProduct);

          if (defaultProduct) {
            setLinkedProductName(defaultProduct.productName || "");
            setFormData((prev) => ({
              ...prev,
              productId: prev.productId || defaultProduct._id,
              ehcCoordination: {
                ...prev.ehcCoordination,
                ehcName:
                  prev.ehcCoordination?.ehcName ||
                  (isEditMode ? prev.ehcCoordination?.ehcName : defaultProduct.productName || ""),
              },
            }));
          }
        } catch (productError) {
          console.warn("Default EHC product not found:", productError?.message || productError);
          setDefaultEHCProduct(null);
        }

        // Fetch tutors - only approved tutors
        const tutorsRes = await api.get("/tutor", {
          params: {
            limit: 100,
            approvalStatus: "APPROVED",
          },
        });

        // Backend returns data in data.data.tutors structure with 'id' field (not '_id')
        let tutorsData = [];
        if (tutorsRes.data && tutorsRes.data.data) {
          if (Array.isArray(tutorsRes.data.data.tutors)) {
            tutorsData = tutorsRes.data.data.tutors;
          } else if (Array.isArray(tutorsRes.data.data)) {
            tutorsData = tutorsRes.data.data;
          }
        }

        console.log("Approved Tutors fetched:", tutorsData.length);
        console.log("First tutor sample:", tutorsData[0]);
        console.log(
          "Tutors structure check:",
          tutorsData.slice(0, 3).map((t) => ({ id: t.id, name: t.fullName }))
        );
        setAllTutors(tutorsData);

        // Fetch parents
        const parentsRes = await api.get("/parent", {
          params: {
            limit: 100,
          },
        });
        const parentsData = Array.isArray(parentsRes.data.data) ? parentsRes.data.data : [];
        console.log("Parents fetched:", parentsData.length);
        setParents(parentsData);

        // Fetch admins - get users and filter by role
        const adminsRes = await api.get("/user", {
          params: {
            limit: 100,
            isActive: true,
          },
        });
        console.log("Admin API full response:", adminsRes.data);

        // User API returns data.users structure
        const usersData = adminsRes.data.data?.users || [];
        console.log("Users data:", usersData);

        const adminsList = Array.isArray(usersData)
          ? usersData.filter((user) => user.role === "ADMIN")
          : [];
        const superAdminsList = Array.isArray(usersData)
          ? usersData.filter((user) => user.role === "SUPER_ADMIN")
          : [];
        const resolvedAdmins = adminsList.length > 0 ? adminsList : superAdminsList;
        console.log("Filtered admins:", adminsList);
        setAdmins(resolvedAdmins);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        toast.error("Failed to load form data");
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Load EHC data in edit mode - only after dropdown data is loaded
  useEffect(() => {
    const loadEHCData = async () => {
      if (isEditMode && id && !dataLoading) {
        setLoading(true);
        try {
          const response = await getEHCById(id);
          if (response.success && response.data) {
            const selectedProductName =
              response.data.productName || response.data.productId?.productName || "";

            // Format the callDate for HTML date input and extract IDs from populated objects
            const formattedData = {
              ...response.data,
              // Extract IDs from populated objects
              parentid: response.data.parentid?._id || response.data.parentid || "",
              studentId: response.data.studentId?._id || response.data.studentId || "",
              tutorId: response.data.tutorId?._id || response.data.tutorId || "",
              userId: response.data.userId?._id || response.data.userId || "",
              productId: response.data.productId?._id || response.data.productId || "",

              // Discover Call - merge with defaults
              discoverCall: {
                callDoneBy:
                  response.data.discoverCall?.callDoneBy?._id ||
                  response.data.discoverCall?.callDoneBy ||
                  "",
                callDate: formatDateForInput(response.data.discoverCall?.callDate),
                callStatus: response.data.discoverCall?.callStatus || "PENDING",
                remarks: response.data.discoverCall?.remarks || "",
              },

              // EHC Coordination - merge with defaults
              ehcCoordination: {
                supervisorId:
                  response.data.ehcCoordination?.supervisorId?._id ||
                  response.data.ehcCoordination?.supervisorId ||
                  "",
                supervisorName:
                  response.data.ehcCoordination?.supervisorName ||
                  response.data.ehcCoordination?.supervisorId?.fullName ||
                  "",
                ehcName: response.data.ehcCoordination?.ehcName || selectedProductName || "",
                timeSlot: response.data.ehcCoordination?.timeSlot || "",
                date: formatDateForInput(response.data.ehcCoordination?.date),
                remarks: response.data.ehcCoordination?.remarks || "",
              },

              // SRC Consultation - merge with defaults
              srcConsultation: {
                meetingDate: formatDateForInput(response.data.srcConsultation?.meetingDate),
                meetingTime: response.data.srcConsultation?.meetingTime || "",
                meetingLink: response.data.srcConsultation?.meetingLink || "",
                academicCoach: response.data.srcConsultation?.academicCoach || "",
              },

              shortTermGoals: Array.isArray(response.data.shortTermGoals)
                ? response.data.shortTermGoals
                : response.data.srcConsultation?.shortTermGoal
                ? [response.data.srcConsultation.shortTermGoal]
                : [""],
              longTermGoals: Array.isArray(response.data.longTermGoals)
                ? response.data.longTermGoals
                : response.data.srcConsultation?.longTermGoal
                ? [response.data.srcConsultation.longTermGoal]
                : [""],

              // Content Knowledge - handle array or convert old format
              contentKnowledge: Array.isArray(response.data.contentKnowledge)
                ? response.data.contentKnowledge
                : response.data.contentKnowledge
                ? [
                    response.data.contentKnowledge.subject1,
                    response.data.contentKnowledge.subject2,
                    response.data.contentKnowledge.subject3,
                  ].filter(Boolean)
                : [{ name: "", understandingLevel: 5, remarks: "" }],

              // Core Parameters - merge with defaults
              coreParameters: {
                overallFoundation: {
                  rating: response.data.coreParameters?.overallFoundation?.rating || 3,
                  remarks: response.data.coreParameters?.overallFoundation?.remarks || "",
                },
                memoryRetention: {
                  rating: response.data.coreParameters?.memoryRetention?.rating || 3,
                  remarks: response.data.coreParameters?.memoryRetention?.remarks || "",
                },
                confidenceInExplaining: {
                  rating: response.data.coreParameters?.confidenceInExplaining?.rating || 3,
                  remarks: response.data.coreParameters?.confidenceInExplaining?.remarks || "",
                },
                writingSkills: {
                  rating: response.data.coreParameters?.writingSkills?.rating || 3,
                  remarks: response.data.coreParameters?.writingSkills?.remarks || "",
                },
                timeManagement: {
                  rating: response.data.coreParameters?.timeManagement?.rating || 3,
                  remarks: response.data.coreParameters?.timeManagement?.remarks || "",
                },
              },

              // Behavioural Parameters - merge with defaults
              behaviouralParameters: {
                regularInPractice: response.data.behaviouralParameters?.regularInPractice || "Yes",
                asksDoubtsActively:
                  response.data.behaviouralParameters?.asksDoubtsActively || "Yes",
                followsStudyPlan: response.data.behaviouralParameters?.followsStudyPlan || "Yes",
              },

              // Parents Review - merge with defaults
              parentsReview: {
                regularityOfEducator: response.data.parentsReview?.regularityOfEducator || "",
                studentSelfStudyTime: response.data.parentsReview?.studentSelfStudyTime || "",
                inTouchWithTutor: response.data.parentsReview?.inTouchWithTutor || "Yes",
                concernsRaisedByParents: response.data.parentsReview?.concernsRaisedByParents || "",
              },

              // SWOT Assessment - merge with defaults
              swotAssessment: {
                strength: response.data.swotAssessment?.strength || "",
                weakness: response.data.swotAssessment?.weakness || "",
                opportunity: response.data.swotAssessment?.opportunity || "",
                threats: response.data.swotAssessment?.threats || "",
              },

              status: response.data.status || "DRAFT",
            };
            setFormData(formattedData);
            setLinkedProductName(selectedProductName);
            setCoachImagePreview(response.data.srcConsultation?.academicCoachImage || null);

            if (response.data.productId && response.data.productId._id) {
              setDefaultEHCProduct((prev) => prev || response.data.productId);
            }
          }
        } catch (error) {
          console.error("Error loading EHC:", error);
          toast.error("Failed to load EHC data");
          navigate("/activities/ehc");
        } finally {
          setLoading(false);
        }
      }
    };
    loadEHCData();
  }, [id, isEditMode, navigate, dataLoading]);

  // Keep productId auto-linked for old EHC records that may not have productId stored.
  useEffect(() => {
    if (!defaultEHCProduct?._id) return;
    if (formData.productId) return;

    setFormData((prev) => ({
      ...prev,
      productId: defaultEHCProduct._id,
      ehcCoordination: {
        ...prev.ehcCoordination,
        ehcName: prev.ehcCoordination?.ehcName || defaultEHCProduct.productName || "",
      },
    }));
  }, [defaultEHCProduct, formData.productId]);

  // Fetch students when parent is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!formData.parentid) {
        setStudents([]);
        setTutors([]);
        setFormData((prev) => ({ ...prev, studentId: "", tutorId: "" }));
        return;
      }

      try {
        const response = await api.get(`/student?parentId=${formData.parentid}`);
        if (response.data.success) {
          const studentsData = Array.isArray(response.data.data) ? response.data.data : [];
          setStudents(studentsData);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        setStudents([]);
      }
    };
    fetchStudents();
  }, [formData.parentid]);

  // Filter tutors by selected student's active and old class-session tutors
  useEffect(() => {
    const fetchStudentTutors = async () => {
      if (!formData.parentid || !formData.studentId) {
        setTutors([]);
        return;
      }

      try {
        const response = await api.get("/classes/sessions/all", {
          params: {
            parentId: formData.parentid,
            limit: 200,
          },
        });

        const sessions = Array.isArray(response?.data?.data) ? response.data.data : [];
        const studentId = String(formData.studentId);

        const matchedSessions = sessions.filter((session) => {
          const sessionStudents = Array.isArray(session?.students) ? session.students : [];
          const hasStudentInStudents = sessionStudents.some(
            (student) => String(student?._id || student?.id || "") === studentId
          );
          const firstStudentId = String(session?.student?._id || session?.student?.id || "");
          return hasStudentInStudents || firstStudentId === studentId;
        });

        const tutorMap = new Map();

        const addTutorIfValid = (rawTutor) => {
          const normalizedTutor = buildTutorOptionData(rawTutor);
          if (!normalizedTutor) return;
          tutorMap.set(normalizedTutor.id, normalizedTutor);
        };

        matchedSessions.forEach((session) => {
          addTutorIfValid(session?.tutor);
          addTutorIfValid(session?.oldTutorDetails);
        });

        allTutors.forEach((tutor) => {
          const normalizedTutor = buildTutorOptionData(tutor);
          if (!normalizedTutor) return;
          if (tutorMap.has(normalizedTutor.id)) {
            tutorMap.set(normalizedTutor.id, {
              ...normalizedTutor,
              ...tutorMap.get(normalizedTutor.id),
            });
          }
        });

        const filteredTutors = Array.from(tutorMap.values());

        // Keep selected tutor in edit mode even if no longer mapped in active sessions
        if (isEditMode && formData.tutorId) {
          const selectedTutorId = String(formData.tutorId);
          if (!tutorMap.has(selectedTutorId)) {
            const selectedFromAll = allTutors.find(
              (tutor) => getTutorIdentifier(tutor) === selectedTutorId
            );
            const selectedTutorData = buildTutorOptionData(
              selectedFromAll || { _id: selectedTutorId }
            );
            if (selectedTutorData) {
              filteredTutors.push(selectedTutorData);
            }
          }
        }

        setTutors(filteredTutors);

        if (formData.tutorId) {
          const selectedStillValid = filteredTutors.some(
            (tutor) => getTutorIdentifier(tutor) === String(formData.tutorId)
          );
          if (!selectedStillValid) {
            setFormData((prev) => ({ ...prev, tutorId: "" }));
          }
        }
      } catch (error) {
        console.error("Error fetching student-specific tutors:", error);
        setTutors([]);
      }
    };

    fetchStudentTutors();
  }, [formData.parentid, formData.studentId, allTutors, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form Data on Submit:", formData);
    console.log("Tutor ID:", formData.tutorId);
    console.log("Student ID:", formData.studentId);
    console.log("Parent ID:", formData.parentid);

    // Validation
    if (!formData.parentid) {
      toast.error("Please select a parent");
      return;
    }

    if (!formData.studentId) {
      toast.error("Please select a student");
      return;
    }

    if (!formData.tutorId) {
      toast.error("Please select a tutor");
      return;
    }

    const resolvedProductId = formData.productId || defaultEHCProduct?._id || "";

    if (!resolvedProductId) {
      toast.error("No EHC product found. Please create product with ehc=true first");
      return;
    }

    if (formData.srcConsultation?.meetingDate && formData.srcConsultation?.meetingTime) {
      const selectedMeetingDateTime = new Date(
        `${formData.srcConsultation.meetingDate}T${formData.srcConsultation.meetingTime}`
      );
      if (!isNaN(selectedMeetingDateTime.getTime()) && selectedMeetingDateTime < new Date()) {
        toast.error("Past meeting date and time is not allowed");
        return;
      }
    }

    // No validation for EHC Coordination and other sections - they are optional on create
    // Can be updated later

    setLoading(true);
    try {
      let response;
      const hasCoachImage = coachImageFiles.length > 0 && coachImageFiles[0]?.file;
      const buildMultipartPayload = (payload) => {
        const multipart = new FormData();
        multipart.append("payload", JSON.stringify(payload));
        if (hasCoachImage) {
          multipart.append("academicCoachImage", coachImageFiles[0].file);
        }
        return multipart;
      };
      if (isEditMode) {
        // Update existing EHC - Remove fields that shouldn't be sent to backend
        const { _id, createdAt, updatedAt, __v, isDeleted, productName, ...updateData } = formData;
        updateData.productId = updateData.productId || resolvedProductId;
        // Clean empty strings and null values for ObjectId fields
        const cleanedData = cleanEmptyFields(updateData);
        response = await updateEHC(
          id,
          hasCoachImage ? buildMultipartPayload(cleanedData) : cleanedData
        );
        toast.success("EHC updated successfully");
      } else {
        // Create new EHC - Clean empty strings and null values
        const createData = { ...formData, productId: resolvedProductId };
        const cleanedData = cleanEmptyFields(createData);
        response = await createEHC(
          hasCoachImage ? buildMultipartPayload(cleanedData) : cleanedData
        );
        toast.success("EHC created successfully");
      }

      console.log("API Response:", response);

      // Navigate back to list after successful save
      setTimeout(() => {
        navigate("/ehc");
      }, 1000);
    } catch (error) {
      console.error("Error saving EHC:", error);
      const errorMessage = error.error || error.message || "Failed to save EHC";
      toast.error(errorMessage);
    } finally {
      if (coachImageFiles.length > 0) {
        setCoachImageFiles([]);
      }
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #ff956f, #F7931E)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(255, 107, 53, 0.25)",
                }}
              >
                <Person sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Basic Information
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Student and Tutor Details
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Parent <span style={{ color: "red" }}>*</span>
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.parentid || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    console.log("Parent selected:", value);
                    setFormData((prev) => ({
                      ...prev,
                      parentid: value,
                      studentId: "",
                      tutorId: "",
                    }));
                  }}
                  options={[
                    { label: "Select Parent", value: "", disabled: dataLoading },
                    ...parents.map((parent) => ({
                      label: `${parent.fullName} - ${parent.phone || parent.email}`,
                      value: parent._id,
                    })),
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Student <span style={{ color: "red" }}>*</span>
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.studentId || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    console.log("Student selected:", value);
                    setFormData((prev) => ({ ...prev, studentId: value, tutorId: "" }));
                  }}
                  options={[
                    {
                      label: !formData.parentid
                        ? "Select Parent First"
                        : students.length === 0
                        ? "No Students Found"
                        : "Select Student",
                      value: "",
                      disabled: !formData.parentid || students.length === 0,
                    },
                    ...students.map((student) => ({
                      label: `${student.name || student.fullName}`,
                      value: student._id,
                    })),
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Tutor <span style={{ color: "red" }}>*</span>
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.tutorId || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    console.log("Tutor selected:", value);
                    setFormData((prev) => {
                      const updated = { ...prev, tutorId: value };
                      console.log("Updated tutorId in formData:", updated.tutorId);
                      return updated;
                    });
                  }}
                  options={[
                    {
                      label: !formData.studentId
                        ? "Select Student First"
                        : tutors.length === 0
                        ? "No Assigned/Old Tutors Found"
                        : "Select Tutor",
                      value: "",
                      disabled: dataLoading || !formData.studentId || tutors.length === 0,
                    },
                    ...(tutors && tutors.length > 0
                      ? tutors.map((tutor) => ({
                          label: `${tutor.fullName} - ${tutor.phone || tutor.email}`,
                          value: getTutorIdentifier(tutor),
                        }))
                      : []),
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  User (Admin)
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.userId || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormData((prev) => ({ ...prev, userId: value }));
                  }}
                  options={[
                    {
                      label: admins.length === 0 ? "No Admins Found" : "Select Admin",
                      value: "",
                      disabled: dataLoading,
                    },
                    ...admins.map((admin) => ({
                      label: `${admin.fullName || admin.name} - ${admin.email}`,
                      value: admin._id,
                    })),
                  ]}
                  width="100%"
                />
              </Grid>
            </Grid>
          </Card>

          {/* Discovery Call */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(78, 205, 196, 0.25)",
                }}
              >
                <Phone sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Discovery Call
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Initial call details
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Call Done By (Admin)
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.discoverCall.callDoneBy || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleNestedChange("discoverCall", "callDoneBy", value);
                  }}
                  options={[
                    { label: "Select Admin", value: "", disabled: dataLoading },
                    ...admins.map((admin) => ({
                      label: `${admin.fullName || admin.name} - ${admin.email}`,
                      value: admin._id,
                    })),
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Call Date
                </ArgonTypography>
                <ArgonInput
                  type="date"
                  fullWidth
                  value={formData.discoverCall.callDate}
                  onChange={(e) => handleNestedChange("discoverCall", "callDate", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Call Status
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.discoverCall.callStatus || "PENDING"}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleNestedChange("discoverCall", "callStatus", value);
                  }}
                  options={[
                    { label: "Pending", value: "PENDING" },
                    { label: "Completed", value: "COMPLETED" },
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Remarks
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter remarks"
                  value={formData.discoverCall.remarks}
                  onChange={(e) => handleNestedChange("discoverCall", "remarks", e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>

          {/* EHC Coordination */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #96CEB4, #88D8C0)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(150, 206, 180, 0.25)",
                }}
              >
                <CalendarToday sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  EHC Coordination
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Schedule and supervisor details - Update later
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Supervisor (Manual)
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  placeholder="Enter supervisor name"
                  value={formData.ehcCoordination.supervisorName || ""}
                  onChange={(e) =>
                    handleNestedChange("ehcCoordination", "supervisorName", e.target.value)
                  }
                />
                {/*
                <Dropdown
                  label=""
                  value={formData.ehcCoordination.supervisorId || ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleNestedChange("ehcCoordination", "supervisorId", value);
                  }}
                  options={[
                    { label: "Select Supervisor", value: "", disabled: dataLoading },
                    ...admins.map((admin) => ({
                      label: `${admin.fullName || admin.name} - ${admin.email}`,
                      value: admin._id,
                    })),
                  ]}
                  width="100%"
                />
                */}
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  EHC Name
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  value={formData.ehcCoordination.ehcName || ""}
                  onChange={(e) =>
                    handleNestedChange("ehcCoordination", "ehcName", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Time Slot
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  placeholder="e.g., 10:00 AM - 11:00 AM"
                  value={formData.ehcCoordination.timeSlot}
                  onChange={(e) =>
                    handleNestedChange("ehcCoordination", "timeSlot", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Date
                </ArgonTypography>
                <ArgonInput
                  type="date"
                  fullWidth
                  value={formData.ehcCoordination.date || ""}
                  onChange={(e) => handleNestedChange("ehcCoordination", "date", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Remarks
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter remarks"
                  value={formData.ehcCoordination.remarks}
                  onChange={(e) => handleNestedChange("ehcCoordination", "remarks", e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>

          {/* Section A - Content Knowledge */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #FF6B6B, #EE5A6F)",
                    borderRadius: "10px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(255, 107, 107, 0.25)",
                  }}
                >
                  <Assignment sx={{ color: "white", fontSize: "20px" }} />
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                    Section A - Content Knowledge
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Subject-wise understanding (0-10 scale) - Update later
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
              <ArgonButton
                variant="gradient"
                color="success"
                size="small"
                onClick={addSubject}
                startIcon={<Add />}
              >
                Add Subject
              </ArgonButton>
            </ArgonBox>

            {formData.contentKnowledge.map((subject, index) => (
              <ArgonBox key={index} mb={3}>
                <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <ArgonTypography variant="body2" fontWeight="600" color="#495057">
                    Subject {index + 1}
                  </ArgonTypography>
                  {formData.contentKnowledge.length > 1 && (
                    <ArgonButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => removeSubject(index)}
                      startIcon={<Delete />}
                    >
                      Remove
                    </ArgonButton>
                  )}
                </ArgonBox>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="500" mb={1}>
                      Subject Name
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="e.g., Mathematics"
                      value={subject.name}
                      onChange={(e) => handleSubjectChange(index, "name", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="500" mb={1}>
                      Understanding Level (0-10)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, max: 10 }}
                      value={subject.understandingLevel}
                      onChange={(e) =>
                        handleSubjectChange(index, "understandingLevel", Number(e.target.value))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="500" mb={1}>
                      Remarks
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="Enter remarks"
                      value={subject.remarks}
                      onChange={(e) => handleSubjectChange(index, "remarks", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </ArgonBox>
            ))}
          </Card>

          {/* Section B - Core Parameters */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #F7B731, #FC5C65)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(247, 183, 49, 0.25)",
                }}
              >
                <Star sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Section B - Core Parameters
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Rate from 1-5 stars - Update later
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            {[
              { key: "overallFoundation", label: "Overall Foundation" },
              { key: "memoryRetention", label: "Memory Retention" },
              { key: "confidenceInExplaining", label: "Confidence in Explaining" },
              { key: "writingSkills", label: "Writing Skills" },
              { key: "timeManagement", label: "Time Management" },
            ].map((param) => (
              <ArgonBox key={param.key} mb={3}>
                <ArgonTypography variant="body2" fontWeight="600" mb={2} color="#495057">
                  {param.label}
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="500" mb={1}>
                      Rating (1-5)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 1, max: 5 }}
                      value={formData.coreParameters[param.key].rating}
                      onChange={(e) =>
                        handleCoreParamChange(param.key, "rating", Number(e.target.value))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="500" mb={1}>
                      Remarks
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="Enter remarks"
                      value={formData.coreParameters[param.key].remarks}
                      onChange={(e) => handleCoreParamChange(param.key, "remarks", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </ArgonBox>
            ))}
          </Card>

          {/* Section C - Behavioural Parameters */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #5F27CD, #341F97)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(95, 39, 205, 0.25)",
                }}
              >
                <Person sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Section C - Behavioural Parameters
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Student behavior assessment - Update later
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              {[
                { key: "regularInPractice", label: "Regular in Practice" },
                { key: "asksDoubtsActively", label: "Asks Doubts Actively" },
                { key: "followsStudyPlan", label: "Follows Study Plan" },
              ].map((param) => (
                <Grid item xs={12} md={4} key={param.key}>
                  <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                    {param.label}
                  </ArgonTypography>
                  <Dropdown
                    label=""
                    value={formData.behaviouralParameters[param.key] || "Yes"}
                    onChange={(event) => {
                      const value = event.target.value;
                      handleNestedChange("behaviouralParameters", param.key, value);
                    }}
                    options={[
                      { label: "Yes", value: "Yes" },
                      { label: "No", value: "No" },
                    ]}
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
          </Card>

          {/* Section D - Parents Review */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #26DE81, #20BF6B)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(38, 222, 129, 0.25)",
                }}
              >
                <Person sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Section D - Parents Review
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Feedback from parents - Update later
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Regularity of Educator
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter feedback"
                  value={formData.parentsReview.regularityOfEducator}
                  onChange={(e) =>
                    handleNestedChange("parentsReview", "regularityOfEducator", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Student Self Study Time
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter details"
                  value={formData.parentsReview.studentSelfStudyTime}
                  onChange={(e) =>
                    handleNestedChange("parentsReview", "studentSelfStudyTime", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  In Touch With Tutor
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.parentsReview.inTouchWithTutor || "Yes"}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleNestedChange("parentsReview", "inTouchWithTutor", value);
                  }}
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Concerns Raised by Parents
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter concerns"
                  value={formData.parentsReview.concernsRaisedByParents}
                  onChange={(e) =>
                    handleNestedChange("parentsReview", "concernsRaisedByParents", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Card>

          {/* SWOT Assessment */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #FD79A8, #FDCB6E)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(253, 121, 168, 0.25)",
                }}
              >
                <Assignment sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  SWOT Assessment
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Strengths, Weaknesses, Opportunities, Threats - Update later
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Strengths
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter strengths"
                  value={formData.swotAssessment.strength}
                  onChange={(e) => handleNestedChange("swotAssessment", "strength", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Weaknesses
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter weaknesses"
                  value={formData.swotAssessment.weakness}
                  onChange={(e) => handleNestedChange("swotAssessment", "weakness", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Opportunities
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter opportunities"
                  value={formData.swotAssessment.opportunity}
                  onChange={(e) =>
                    handleNestedChange("swotAssessment", "opportunity", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Threats
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter threats"
                  value={formData.swotAssessment.threats}
                  onChange={(e) => handleNestedChange("swotAssessment", "threats", e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>

          {/* SRC - Strategic Roadmapping And Consultation */}
          <Card sx={cardStyle}>
            <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
              <ArgonBox
                sx={{
                  background: "linear-gradient(45deg, #6A89CC, #4A69BD)",
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(74, 105, 189, 0.25)",
                }}
              >
                <Assignment sx={{ color: "white", fontSize: "20px" }} />
              </ArgonBox>
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  SRC (Strategic Roadmapping And Consultation)
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Strategic meeting and academic coach details
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>

            <Grid container spacing={2}>
              {(() => {
                const minDate = getCurrentDateString();
                const minTimeForToday =
                  formData.srcConsultation.meetingDate === minDate
                    ? getCurrentTimeString()
                    : undefined;

                return (
                  <>
                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Date of Meeting
                      </ArgonTypography>
                      <ArgonInput
                        type="date"
                        fullWidth
                        value={formData.srcConsultation.meetingDate}
                        onChange={(e) => handleSrcDateChange(e.target.value)}
                        inputProps={{ min: minDate }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Time of Meeting
                      </ArgonTypography>
                      <ArgonInput
                        type="time"
                        fullWidth
                        value={formData.srcConsultation.meetingTime}
                        onChange={(e) => handleSrcTimeChange(e.target.value)}
                        inputProps={minTimeForToday ? { min: minTimeForToday } : {}}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Meeting Link
                      </ArgonTypography>
                      <ArgonInput
                        fullWidth
                        placeholder="https://meet.google.com/..."
                        value={formData.srcConsultation.meetingLink}
                        onChange={(e) =>
                          handleNestedChange("srcConsultation", "meetingLink", e.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Academic Coach (Manual)
                      </ArgonTypography>
                      <ArgonInput
                        fullWidth
                        placeholder="Enter academic coach name"
                        value={formData.srcConsultation.academicCoach}
                        onChange={(e) =>
                          handleNestedChange("srcConsultation", "academicCoach", e.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Academic Coach Image
                      </ArgonTypography>
                      {coachImagePreview && (
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="caption" color="text" mb={1}>
                            Current Image:
                          </ArgonTypography>
                          <ArgonBox>
                            <img
                              src={coachImagePreview}
                              alt="Academic Coach"
                              height="80px"
                              width="140px"
                              style={{
                                border: "1px solid #e0e7ff",
                                borderRadius: "8px",
                                objectFit: "cover",
                              }}
                            />
                          </ArgonBox>
                        </ArgonBox>
                      )}
                      <FileUploader
                        label="Upload Coach Image"
                        files={coachImageFiles}
                        setFiles={setCoachImageFiles}
                        accept="image/*"
                        multiple={false}
                        Width="100%"
                        Height={120}
                      />
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </Card>

          <Card sx={cardStyle}>
            <Grid item xs={12}>
              <ArgonBox mb={1}>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Short-term Goal (5-7 days)
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Add multiple short-term goal points
                </ArgonTypography>
              </ArgonBox>

              {(formData.shortTermGoals || [""]).map((point, index) => (
                <ArgonBox key={`short-${index}`} display="flex" alignItems="center" mb={1}>
                  <ArgonInput
                    placeholder="Enter short-term goal point"
                    value={point}
                    onChange={(e) => handleGoalItemChange("shortTermGoals", index, e.target.value)}
                    fullWidth
                    size="small"
                  />
                  {(formData.shortTermGoals || []).length > 1 && (
                    <ArgonButton
                      onClick={() => removeGoalItem("shortTermGoals", index, "short-term")}
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <HighlightOff />
                    </ArgonButton>
                  )}
                </ArgonBox>
              ))}
              <ArgonButton
                onClick={() => addGoalItem("shortTermGoals")}
                variant="outlined"
                color="primary"
                size="small"
              >
                Add Point
              </ArgonButton>
            </Grid>
          </Card>

          <Card sx={cardStyle}>
            <Grid item xs={12}>
              <ArgonBox mb={1}>
                <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                  Long-term Goal (20-25 days)
                </ArgonTypography>
                <ArgonTypography variant="body2" color="#6c757d">
                  Add multiple long-term goal points
                </ArgonTypography>
              </ArgonBox>

              {(formData.longTermGoals || [""]).map((point, index) => (
                <ArgonBox key={`long-${index}`} display="flex" alignItems="center" mb={1}>
                  <ArgonInput
                    placeholder="Enter long-term goal point"
                    value={point}
                    onChange={(e) => handleGoalItemChange("longTermGoals", index, e.target.value)}
                    fullWidth
                    size="small"
                  />
                  {(formData.longTermGoals || []).length > 1 && (
                    <ArgonButton
                      onClick={() => removeGoalItem("longTermGoals", index, "long-term")}
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      <HighlightOff />
                    </ArgonButton>
                  )}
                </ArgonBox>
              ))}
              <ArgonButton
                onClick={() => addGoalItem("longTermGoals")}
                variant="outlined"
                color="primary"
                size="small"
              >
                Add Point
              </ArgonButton>
            </Grid>
          </Card>

          {/* Status */}
          <Card sx={cardStyle}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                  Status
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={formData.status || "DRAFT"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormData((prev) => ({ ...prev, status: value }));
                  }}
                  options={[
                    { label: "Draft", value: "DRAFT" },
                    { label: "Completed", value: "COMPLETED" },
                    { label: "Reviewed", value: "REVIEWED" },
                  ]}
                  width="100%"
                />
              </Grid>
            </Grid>
          </Card>

          {/* Submit Buttons */}
          <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <ArgonButton variant="outlined" color="secondary" onClick={() => navigate("/ehc")}>
              Cancel
            </ArgonButton>
            <ArgonButton variant="gradient" color="info" type="submit" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update EHC" : "Create EHC"}
            </ArgonButton>
          </ArgonBox>
        </form>
      </ArgonBox>
    </DashboardLayout>
  );
};

export default AddEHC;
